import {
    AudioOutlined,
    DownloadOutlined,
    FileExcelOutlined,
    FileImageOutlined,
    FileOutlined,
    FilePdfOutlined,
    FileTextOutlined,
    FileWordOutlined,
    FileZipOutlined,
    FolderOutlined,
    VideoCameraOutlined,
} from '@ant-design/icons';
import { Empty, Space, Table, Tag, Typography } from 'antd';
import type { ColumnType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CategoryType } from '../../../../../lib/global-type';
import { axiosInstance } from '../services/axios-instance';
import { downloadFile } from '../services/download-file';
import { AppDispatch, RootState } from '../store';
import { setterSharingDocument } from '../store/sharing-document-slice';
import { DocumentsResponseType, SharingDocumentType } from '../types/types';
import { TreeNodeWithMetadataType } from './directory-tree-wrapper';
import DocumentMarkupModal from './document-markup-modal';

const { Text } = Typography;

export const CATEGORY_TYPE = 'category';

const isCategory = (item: any, key = 'type'): item is CategoryType => {
    return item?.[key] === CATEGORY_TYPE;
};

export const DocumentTable = () => {
    const dispatch = useDispatch<AppDispatch>();
    const listDocument = useSelector((state: RootState) => state.sharingDocument.listDocument);
    const documentTree = useSelector((state: RootState) => state.sharingDocument.documentTree);
    const allTreeKeys = useSelector((state: RootState) => state.sharingDocument.allTreeKeys);

    const [selectedTreeKey] = useSelector((state: RootState) => state.sharingDocument.selectedTreeKey);

    const findNodeTree = (key: string, tree: TreeNodeWithMetadataType[]): TreeNodeWithMetadataType | null => {
        for (const node of tree) {
            if (node.key === key) {
                return node;
            }

            if (node.children && node.children.length > 0) {
                const found = findNodeTree(key, node.children as TreeNodeWithMetadataType[]);
                if (found) {
                    return found;
                }
            }
        }

        return null;
    };

    const nodeTreeChildren = findNodeTree(selectedTreeKey, documentTree)?.children || [];

    const actualListDocument: SharingDocumentType[] = [
        ...nodeTreeChildren.map((node) => ({
            id: node.key as string,
            category_id: node.key as string,
            name: node.title as string,
            path: '',
            type: 'category' as const,
            extension: 'folder',
            mime_type: 'folder',
            size: 0,
            version: 1,
            created_at: '',
            updated_at: '',
            url: '',
            customer: '',
        })),
        ...listDocument.filter((document) => {
            return (
                +document.category_id === +selectedTreeKey || !allTreeKeys.includes(document.category_id?.toString())
            );
        }),
    ];

    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    useEffect(() => {
        const fetchDocuments = async (customer: string) => {
            setLoading(true);
            try {
                const response = await axiosInstance.get<DocumentsResponseType>('/documents', {
                    params: { customer },
                });

                const listDocument = response.data.documents.map((document) => ({
                    ...document,
                    customer: customer,
                    type: 'document' as const,
                }));
                dispatch(setterSharingDocument({ listDocument }));
            } catch (error) {
                console.error('Failed to fetch documents:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchDocuments('smxcon');
    }, []);

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
    };

    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    };

    const getFileIcon = (extension: string) => {
        const ext = extension.toLowerCase();

        if (ext === 'folder') return <FolderOutlined style={{ color: '#faad14' }} />;

        // Images
        if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg', 'webp'].includes(ext)) {
            return <FileImageOutlined style={{ color: '#52c41a' }} />;
        }

        // PDF
        if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#f5222d' }} />;

        // Word documents
        if (['doc', 'docx'].includes(ext)) {
            return <FileWordOutlined style={{ color: '#1890ff' }} />;
        }

        // Excel documents
        if (['xls', 'xlsx', 'csv'].includes(ext)) {
            return <FileExcelOutlined style={{ color: '#52c41a' }} />;
        }

        // Text files
        if (['txt', 'md', 'log'].includes(ext)) {
            return <FileTextOutlined style={{ color: '#8c8c8c' }} />;
        }

        // Archives
        if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) {
            return <FileZipOutlined style={{ color: '#722ed1' }} />;
        }

        // Video
        if (['mp4', 'avi', 'mkv', 'mov', 'wmv', 'flv', 'webm'].includes(ext)) {
            return <VideoCameraOutlined style={{ color: '#eb2f96' }} />;
        }

        // Audio
        if (['mp3', 'wav', 'flac', 'aac', 'ogg', 'wma'].includes(ext)) {
            return <AudioOutlined style={{ color: '#13c2c2' }} />;
        }

        // Default
        return <FileOutlined />;
    };

    const columns: ColumnType<SharingDocumentType>[] = [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            // width: '50%',
            ellipsis: true,
            render: (name: string, record: SharingDocumentType) => {
                return (
                    <Space>
                        {getFileIcon(record.extension)}
                        <Text>{name}</Text>
                    </Space>
                );
            },
        },
        {
            title: 'Type',
            dataIndex: 'extension',
            key: 'extension',
            width: 100,
            render: (extension: string) => <Tag color="blue">{extension.toUpperCase()}</Tag>,
        },
        {
            title: 'Size',
            dataIndex: 'size',
            key: 'size',
            width: 120,
            render: (size: number) => (size ? formatFileSize(size) : ''),
        },
        {
            title: 'Updated',
            dataIndex: 'updated_at',
            key: 'updated_at',
            width: 180,
            render: (date: string) => (date ? formatDate(date) : ''),
        },
        {
            title: 'Action',
            key: 'action',
            width: 100,
            render: (_: any, record: SharingDocumentType) => (
                <Space>
                    {record.type !== CATEGORY_TYPE && (
                        <DownloadOutlined
                            style={{ cursor: 'pointer', fontSize: 16 }}
                            onClick={async () => {
                                setLoading(true);
                                const response = await axiosInstance.get<SharingDocumentType>(`/document`, {
                                    params: {
                                        customer: record.customer,
                                        id: record.id,
                                    },
                                });

                                const url = response.data.url;
                                setLoading(false);
                                if (!url) return;

                                try {
                                    const filename = record.name || 'file';
                                    await downloadFile(url, filename);
                                } catch (err) {
                                    console.error('Failed to download file:', err);
                                }
                            }}
                        />
                    )}
                </Space>
            ),
        },
    ];

    const handleDocumentOpen = async (record: SharingDocumentType) => {
        setLoading(true);
        const response = await axiosInstance.get<SharingDocumentType>(`/document`, {
            params: {
                customer: record.customer,
                id: record.id,
            },
        });
        setLoading(false);
        await dispatch(setterSharingDocument({ openDocument: response.data }));
    };

    const handleCategoryOpen = (record: SharingDocumentType) => {
        dispatch(setterSharingDocument({ selectedTreeKey: [record.id] }));
    };

    const onRow = (record: SharingDocumentType) => {
        return {
            onDoubleClick: () => {
                if (isCategory(record)) {
                    handleCategoryOpen(record);
                } else {
                    handleDocumentOpen(record);
                }
            },
        };
    };

    return (
        <>
            <Table<SharingDocumentType>
                columns={columns}
                dataSource={actualListDocument}
                loading={loading}
                rowKey="id"
                pagination={{
                    current: currentPage,
                    pageSize: pageSize,
                    total: actualListDocument.length,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} documents`,
                    onChange: (page, size) => {
                        setCurrentPage(page);
                        setPageSize(size);
                    },
                }}
                scroll={{ y: 'calc(100vh - 224px)' }}
                tableLayout="fixed"
                onRow={onRow}
                locale={{
                    emptyText: <Empty />,
                }}
            />
            <DocumentMarkupModal />
        </>
    );
};
